import { ChangeDetectionStrategy, Component, OnInit, computed, effect, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FavouriteArtistsService, toGridArtist } from '../favourite-artists.service';
import { ArtistGridComponent } from '../shared/artist-grid.component';
import { PagerComponent } from '../shared/pager.component';
import { matchesQuery, pageCount, pageSlice } from '../shared/album-list';
import { loadSavedState, saveState } from '../shared/saved-state';

export type ArtistSort = 'popular' | 'az' | 'added';

const STATE_KEY = 'favouriteArtists.filters';
const SORTS: ArtistSort[] = ['popular', 'az', 'added'];
const DEFAULT_STATE = { query: '', sort: 'popular', page: 1 };

// The whole saved pool (up to 400 artists), searched and sorted locally
export const FAVOURITE_ARTISTS_PAGE_SIZE = 20;
export const FAVOURITE_ARTISTS_MAX_PAGES = 20;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-favourite-artists',
  templateUrl: './favourite-artists.component.html',
  styleUrls: ['./favourite-artists.component.css'],
  imports: [RouterLink, ArtistGridComponent, PagerComponent]
})
export class FavouriteArtistsComponent implements OnInit {
  private saved = loadSavedState(STATE_KEY, DEFAULT_STATE);

  query = signal(this.saved.query);
  sort = signal<ArtistSort>(SORTS.includes(this.saved.sort as ArtistSort) ? this.saved.sort as ArtistSort : 'popular');
  page = signal(Math.max(1, Math.floor(this.saved.page) || 1));

  loading = signal(true);
  error = signal<string | null>(null);

  pool = computed(() => (this.favourites.artists() ?? []).map(toGridArtist));

  filtered = computed(() => {
    let list = this.pool();
    const q = this.query().trim();
    if (q) list = list.filter(a => matchesQuery({ name: a.name }, q));
    const sorted = [...list];
    switch (this.sort()) {
      case 'az': return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'added': return sorted.sort((a, b) => (b.addedAt ?? '').localeCompare(a.addedAt ?? ''));
      default: return sorted.sort((a, b) => (b.fans ?? 0) - (a.fans ?? 0));
    }
  });
  pages = computed(() => pageCount(this.filtered().length, FAVOURITE_ARTISTS_PAGE_SIZE, FAVOURITE_ARTISTS_MAX_PAGES));
  visible = computed(() => pageSlice(this.filtered(), this.page(), FAVOURITE_ARTISTS_PAGE_SIZE));

  constructor(protected favourites: FavouriteArtistsService) {
    effect(() => saveState(STATE_KEY, { query: this.query(), sort: this.sort(), page: this.page() }));
    // Removing artists can shrink the list below the current page
    effect(() => {
      const pages = this.pages();
      if (!this.loading() && pages > 0 && this.page() > pages) this.page.set(pages);
    });
  }

  ngOnInit() {
    this.favourites.loadAll().subscribe({
      next: () => this.loading.set(false),
      error: (err) => {
        console.error('Error loading favourite artists:', err);
        this.error.set('Could not load your favourite artists. Please try again.');
        this.loading.set(false);
      }
    });
  }

  setQuery(text: string) {
    this.query.set(text);
    this.page.set(1);
  }

  setSort(sort: ArtistSort) {
    this.sort.set(sort);
    this.page.set(1);
  }

  setPage(page: number) {
    this.page.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  clearFilters() {
    this.query.set('');
    this.sort.set('popular');
    this.page.set(1);
  }

  toggleFavourite(artist: { id: string; name: string }) {
    this.favourites.toggle(artist.id, artist.name);
  }
}
